/**
 * Composite sub-field KINDS — the client half of a vocabulary the server
 * already serves.
 *
 * Every composite attribute's `sub_structure` carries, per sub-field, the
 * constraint kind its Python codec enforces (`country-iso2`, `latitude`,
 * `text-stripped`, … — `KindLiteral` in `monad/types_catalog/_bases.py`,
 * serialized by `enrich_sub_structure`). The GUI received that all along and
 * read only `name` and `type === 'number'`.
 *
 * SEN-1780: the cost of ignoring it was a workspace's first Site. The country
 * input was labelled "country" with no hint, the user typed "Denmark", the
 * codec accepted only "DK", and the refusal that came back is deliberately
 * static ("The supplied value could not be encoded for its attribute's type."
 * — SEN-1071 keeps wire messages free of internal state), so the GUI could not
 * even say which field was wrong.
 *
 * So: one registration per kind, dispatched on the SERVED kind. Nothing here
 * branches on a sub-field's name, and a kind nobody has registered yet passes
 * through untouched rather than blocking a write.
 */

export interface SubFieldKindSpec {
  /** `<input type>` to render. */
  inputType: 'text' | 'number'
  /** Shown in the empty input — an example of the accepted form. */
  placeholder?: string
  /** One line under the label, stating the rule in words. */
  help?: string
  /** Draft string → the value sent on the wire. */
  coerce: (raw: string) => unknown
  /** Draft string → a customer-readable reason, or null when acceptable.
   *  `label` is the sub-field's displayed name so the message can name it. */
  validate: (raw: string, label: string) => string | null
}

/** ISO 3166-1 alpha-2 — the closed register of assigned country codes.
 *  Shape is not membership: "ZZ" is two letters and no country, and letting
 *  it through stores a country nobody can act on. Same reasoning as the ISO
 *  4217 register on the server side (`monad/iso4217.py`).
 *
 *  Asymmetry, deliberate and recorded: the server's `country-iso2` codec
 *  checks only for two alphabetic characters, so it would ACCEPT "ZZ". This
 *  list is therefore stricter than the wire contract — it prevents
 *  meaningless data rather than merely predicting a refusal. Moving the
 *  authoritative check into the codec is the symmetric fix and belongs with
 *  the codec, not here. */
export const ISO_3166_1_ALPHA2: ReadonlySet<string> = new Set(
  `AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ
   BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ
   CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ
   DE DJ DK DM DO DZ EC EE EG EH ER ES ET
   FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY
   HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT
   JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ
   LA LB LC LI LK LR LS LT LU LV LY
   MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ
   NA NC NE NF NG NI NL NO NP NR NU NZ OM
   PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA
   RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ
   TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ
   UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW`.split(/\s+/),
)

/** Common country names (and the habitual near-misses) → ISO 3166-1 alpha-2.
 *  A `Map`, not an object: indexing a plain object answers for `constructor`,
 *  `__proto__` and `toString`, so those passed validation and serialised to
 *  nonsense. Deliberately small and NOT geocoding — it turns the most common
 *  near-miss into a success, and anything it does not know is refused rather
 *  than guessed at. */
const COUNTRY_ALIASES: ReadonlyMap<string, string> = new Map([
  ['denmark', 'DK'], ['danmark', 'DK'],
  ['sweden', 'SE'], ['sverige', 'SE'],
  ['norway', 'NO'], ['norge', 'NO'],
  ['finland', 'FI'], ['suomi', 'FI'],
  ['iceland', 'IS'], ['island', 'IS'],
  ['germany', 'DE'], ['deutschland', 'DE'], ['tyskland', 'DE'],
  ['netherlands', 'NL'], ['nederland', 'NL'], ['holland', 'NL'],
  ['belgium', 'BE'], ['belgien', 'BE'],
  ['france', 'FR'], ['frankrig', 'FR'],
  ['spain', 'ES'], ['spanien', 'ES'],
  ['italy', 'IT'], ['italien', 'IT'],
  ['poland', 'PL'], ['polen', 'PL'],
  // "UK" is two letters but NOT an assigned alpha-2 code, so aliases must be
  // consulted before the shape rule — a shape-first order silently produced
  // a country that does not exist.
  ['united kingdom', 'GB'], ['uk', 'GB'], ['england', 'GB'], ['storbritannien', 'GB'],
  ['ireland', 'IE'], ['irland', 'IE'],
  ['united states', 'US'], ['usa', 'US'], ['united states of america', 'US'],
])

/** The ISO-2 code a draft resolves to, or null when it resolves to none.
 *  ALIAS first, then shape AND membership. */
function toIso2(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  const alias = COUNTRY_ALIASES.get(s.toLowerCase())
  if (alias) return alias
  if (!/^[A-Za-z]{2}$/.test(s)) return null
  const code = s.toUpperCase()
  return ISO_3166_1_ALPHA2.has(code) ? code : null
}

function coordinate(min: number, max: number): SubFieldKindSpec {
  return {
    inputType: 'number',
    help: `A number between ${min} and ${max}`,
    coerce: (raw) => Number(raw.trim()),
    validate: (raw, label) => {
      const n = Number(raw.trim())
      if (!Number.isFinite(n)) return `${label} must be a number, for example 56.15.`
      if (n < min || n > max) return `${label} must be between ${min} and ${max}.`
      return null
    },
  }
}

/** Fallback for any kind not registered here: send the trimmed string,
 *  refuse nothing. A kind added to the catalogue tomorrow must not make
 *  today's GUI reject valid input. */
const PASS_THROUGH: SubFieldKindSpec = {
  inputType: 'text',
  coerce: (raw) => raw.trim(),
  validate: () => null,
}

const SUB_FIELD_KINDS: Record<string, SubFieldKindSpec> = {
  'country-iso2': {
    inputType: 'text',
    placeholder: 'DK',
    help: 'Two-letter country code (ISO 3166-1), e.g. DK',
    coerce: (raw) => toIso2(raw) ?? raw.trim().toUpperCase(),
    validate: (raw, label) =>
      toIso2(raw)
        ? null
        : `${label} must be a two-letter country code such as DK — "${raw.trim()}" is not one.`,
  },
  latitude: coordinate(-90, 90),
  'latitude-strict': coordinate(-90, 90),
  longitude: coordinate(-180, 180),
  'longitude-strict': coordinate(-180, 180),
  number: {
    inputType: 'number',
    coerce: (raw) => Number(raw.trim()),
    validate: (raw, label) =>
      Number.isFinite(Number(raw.trim())) ? null : `${label} must be a number.`,
  },
}

export function resolveSubFieldKind(kind: string | undefined): SubFieldKindSpec {
  return (kind && SUB_FIELD_KINDS[kind]) || PASS_THROUGH
}

export function validateSubValue(kind: string | undefined, raw: string, label: string): string | null {
  if (raw.trim() === '') return null // emptiness is the required-check's business
  return resolveSubFieldKind(kind).validate(raw, label)
}

export function coerceSubValue(kind: string | undefined, raw: string): unknown {
  return resolveSubFieldKind(kind).coerce(raw)
}

export interface SubFieldLike {
  name: string
  /** Generic storage kind (`text`, `number`). */
  type?: string
  /** The served CONSTRAINT kind — preferred over `type` when present. */
  kind?: string | null
  required?: boolean
}

/** The kind to dispatch on: the served constraint when there is one, else the
 *  generic storage type. One rule, in one place, so no consumer re-derives it. */
export const kindOf = (sf: SubFieldLike): string | undefined => sf.kind ?? sf.type

/**
 * Every reason this composite draft would be refused — all of them, so one
 * round trip fixes the lot instead of the user discovering them one at a
 * time. Empty array means "the codec will accept this", which is the whole
 * point: never send a value we already know is refused.
 */
export function validateComposite(
  subFields: SubFieldLike[],
  draft: Record<string, string>,
): string[] {
  const out: string[] = []
  for (const sf of subFields) {
    const raw = (draft[sf.name] ?? '').trim()
    if (raw === '') {
      if (sf.required) out.push(`${sf.name} is required.`)
      continue
    }
    const msg = validateSubValue(kindOf(sf), raw, sf.name)
    if (msg) out.push(msg)
  }
  return out
}

/** The wire value for a composite draft: coerced per served kind, empties
 *  omitted, `null` when nothing was filled in. */
export function buildCompositeValue(
  subFields: SubFieldLike[],
  draft: Record<string, string>,
): Record<string, unknown> | null {
  const out: Record<string, unknown> = {}
  for (const sf of subFields) {
    const raw = (draft[sf.name] ?? '').trim()
    if (raw === '') continue
    out[sf.name] = coerceSubValue(kindOf(sf), raw)
  }
  return Object.keys(out).length > 0 ? out : null
}
